import type { BookingDetail, BookingPatch } from '../types/VendorBookingRecord';

/** The editable fields of a booking page, as text the inputs hold. Status and the signed-contract flag are not here: they apply at once. */
export interface BookingForm {
  event_id: string;
  booking_date: string;
  confirmation_ref: string;
  contract_date: string;
  contract_link: string;
  special_terms: string;
  contact_method: string;
  contact_notes: string;
  last_contact_date: string;
  next_followup_date: string;
  deposit_percent: string;
  deposit_due_date: string;
  balance_due_date: string;
  contingency_amount: string;
  total_amount: string;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const day = (v: string | null | undefined): string => str(v).slice(0, 10);

export const formFromBooking = (b: BookingDetail): BookingForm => ({
  event_id: str(b.event_id),
  booking_date: day(b.booking_date),
  confirmation_ref: str(b.confirmation_ref),
  contract_date: day(b.contract_date),
  contract_link: str(b.contract_link),
  special_terms: str(b.special_terms),
  contact_method: str(b.contact_method),
  contact_notes: str(b.contact_notes),
  last_contact_date: day(b.last_contact_date),
  next_followup_date: day(b.next_followup_date),
  deposit_percent: b.deposit_percent === null || b.deposit_percent === undefined ? '' : String(b.deposit_percent),
  deposit_due_date: day(b.deposit_due_date),
  balance_due_date: day(b.balance_due_date),
  contingency_amount: str(b.contingency_amount),
  total_amount: str(b.total_amount),
});

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

interface Options {
  /** Money fields are only ever sent by someone who may see money: a null read back must never overwrite the real amount. */
  canViewFinancials: boolean;
  /** The booking has no lines, so its total is its own to set. */
  noLines: boolean;
}

/** Only what changed, in the shape PATCH accepts. */
export function buildBookingPatch(form: BookingForm, booking: BookingDetail, opts: Options): BookingPatch {
  const base = formFromBooking(booking);
  const patch: BookingPatch = {};
  const changed = (k: keyof BookingForm) => form[k].trim() !== base[k].trim();
  for (const k of [
    'event_id', 'booking_date', 'confirmation_ref', 'contract_date', 'contract_link', 'special_terms', 'contact_method', 'contact_notes',
    'last_contact_date', 'next_followup_date',
  ] as const) {
    if (changed(k)) patch[k] = orNull(form[k]);
  }
  if (opts.canViewFinancials) {
    if (changed('deposit_due_date')) patch.deposit_due_date = orNull(form.deposit_due_date);
    if (changed('balance_due_date')) patch.balance_due_date = orNull(form.balance_due_date);
    if (changed('deposit_percent') && form.deposit_percent.trim() !== '') patch.deposit_percent = Number(form.deposit_percent);
    if (changed('contingency_amount')) patch.contingency_amount = orNull(form.contingency_amount);
    if (opts.noLines && changed('total_amount') && form.total_amount.trim() !== '') patch.total_amount = form.total_amount.trim();
  }
  return patch;
}

const AMOUNT = /^\d{1,10}(\.\d{1,2})?$/;

/** Field-level problems found before sending, by field name. */
export function validateBookingForm(form: BookingForm, opts: Options): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.contract_link.trim() && !/^https?:\/\//i.test(form.contract_link.trim())) errors.contract_link = 'Start the link with http:// or https://.';
  if (opts.canViewFinancials) {
    const pct = form.deposit_percent.trim();
    if (pct !== '' && !(/^\d{1,3}$/.test(pct) && Number(pct) <= 100)) errors.deposit_percent = 'Enter a whole number from 0 to 100.';
    for (const k of ['contingency_amount', 'total_amount'] as const) {
      const v = form[k].trim();
      if (v !== '' && !AMOUNT.test(v)) errors[k] = 'Enter an amount such as 1250 or 1250.50.';
    }
  }
  return errors;
}
